import { TeamLearnerResponse, TeamType, useRemoveTeamLearner } from 'api';
import { useState } from 'react';
import {
  ColourEnergyIndicator,
  TextV2,
} from '@insights-ltd/design-library/components';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useQueryClient } from '@tanstack/react-query';
import { booleanizeCheckedItems } from 'utils/mappers/entityMappers';
import { styled } from '@mui/material';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import LinkEmail from 'components/LinkEmail/LinkEmail';
import { LatestProfileText } from 'utils/LatestProfileText';
import { getSupportedDownloadDialect } from 'variables/dialects';
import DownloadProgressDialog from 'components/DownloadProgressDialog/DownloadProgressDialog';
import downloadProfile from 'api/downloadProfile';
import { Dialects } from 'types/dialects';
import { ApiPaginationProps } from 'components/Pagination/Pagination';
import { Paginator } from 'components/hooks/usePaginationV2';
import { DataTable } from 'components/DataTable/DataTableV2';
import { useRequestErrorContext } from 'components/RequestErrorDialog/RequestErrorProvider';
import { TransformedLearnerResponse } from './learnerType';
import LearnersListEmptyMsg from './LearnersListEmptyMsg';
const StyledDiv = styled('div')({
  '> div': {
    height: '35px',
  },
});

const StyledListItemWrapper = styled('div')(({ theme }) => ({
  '> * + *': {
    marginTop: theme.spacing(spacingSizeMap.XS),
  },
}));

const StyledTable = styled(DataTable)({
  'tr td': {
    verticalAlign: 'top',
  },
  'tr > td:nth-of-type(1) > span > span': {
    paddingTop: 0,
  },
  'tr > td:last-of-type': {
    paddingTop: '18px',
    paddingBottom: '18px',
  },
  'tr > td button': {
    paddingBlock: 0,
  },
});

type TeamLearnerResponseDataStructure = Partial<
  Record<keyof TeamLearnerResponse & 'colourScores' & 'organisation', string>
>;

type LearnersListProps = {
  evaluationIds: string[];
  teamId: string;
  checkedItems?: Record<string, TransformedLearnerResponse>;
  setCheckedItems?: React.Dispatch<
    React.SetStateAction<Record<string, TransformedLearnerResponse>>
  >;
  removeCheckedItems?: (ids: string[]) => void;
  migrated: boolean;
  selectable: boolean;
  type?: TeamType;
  onPageChange?: ({
    pageNumber,
    lastPage,
  }: {
    pageNumber: number;
    lastPage?: boolean;
  }) => void;
  paginator: Paginator<TransformedLearnerResponse>;
  apiPaginationProps?: ApiPaginationProps;
};

const RemoveTeamMember = ({
  id,
  onClick,
  disabled = false,
}: {
  id: string;
  onClick: (id: string) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Button
      aria-label={t(
        'ui.event-management.events.add-learners-and-contributors.contributor.delete.label',
      )}
      data-testid="remove-user"
      sx={() => ({
        padding: 0,
        minWidth: 0,
      })}
      onClick={() => onClick(id)}
      disabled={disabled}
    >
      <TextV2 variant="bodyBold" color="error">
        {t('ui.event-management.invite-list.remove-user')}
      </TextV2>
    </Button>
  );
};

const TeamLearners = ({
  teamId,
  checkedItems = {},
  setCheckedItems,
  removeCheckedItems,
  migrated,
  selectable,
  type = 'SINGLE',
  onPageChange,
  paginator,
  apiPaginationProps,
}: LearnersListProps) => {
  const [showDownloadProfileDialog, setShowDownloadProfileDialog] =
    useState(false);
  const { openErrorModal } = useRequestErrorContext();
  const learners = paginator.items;

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: removeLearner } = useRemoveTeamLearner(
    queryClient,
    teamId,
  );
  const handleRemoveTeamMember = async (evaluationId: string) => {
    const learnerId =
      learners.find(({ id }) => id === evaluationId)?.learnerId ?? '';
    await removeLearner({ learnerId, teamId });
    removeCheckedItems?.([evaluationId]);
  };

  const onDownloadProfile = async (
    learnerId: string,
    profileId: string,
    dialect: Dialects,
  ) => {
    try {
      setShowDownloadProfileDialog(true);
      await downloadProfile(learnerId, profileId, dialect, 'NAMED');
      setShowDownloadProfileDialog(false);
    } catch {
      setShowDownloadProfileDialog(false);
      openErrorModal();
    }
  };

  const dataStructure: TeamLearnerResponseDataStructure =
    type === 'SINGLE'
      ? {
          fullName: t('ui.event-management.learners.heading.full-name'),
          colourScores: t(
            'ui.event-management.learners.heading.colour-energies',
          ),
          primaryEmail: t('ui.event-management.learners.heading.email'),
          latestProfile: t(
            'ui.event-management.learners.heading.latest-profile',
          ),
        }
      : {
          fullName: t('ui.event-management.learners.heading.full-name'),
          primaryEmail: t('ui.event-management.learners.heading.email'),
          latestProfile: t(
            'ui.event-management.learners.heading.latest-profile',
          ),
          organisation: t('ui.event-management.learners.heading.organisation'),
        };

  const singleOrgColumnPredicate = (
    columnName: keyof (TeamLearnerResponse & {
      learnerId: string;
      colourScores: string;
      organisation: string;
    }),
    columnValue: string | number,
    props: Record<any, any>,
  ) => {
    const { fullName, learnerId, latestProfile, latestEvaluation } =
      props as TeamLearnerResponse & {
        learnerId: string;
      };

    switch (columnName) {
      case 'fullName':
        return (
          <Link
            component={RouterLink}
            data-testid="learner-detail-link"
            to={`/learners/${latestEvaluation.organisation.id}/${learnerId}`}
          >
            <Box
              sx={{
                display: 'flex',
              }}
            >
              <TextV2 variant="bodyBold" color="primary">
                {fullName}
              </TextV2>
            </Box>
          </Link>
        );
      case 'colourScores':
        return (
          <ColourEnergyIndicator
            {...latestProfile?.colourScores?.conscious}
            data-testid="colour-energies"
          />
        );
      case 'primaryEmail':
        return (
          <LinkEmail
            sx={(theme) => ({
              fontWeight: `${theme.typography.fontWeightMedium} !important`,
            })}
            email={columnValue as string}
          />
        );

      case 'latestProfile':
        return (
          <LatestProfileText
            migrated={migrated}
            latestProfile={latestProfile}
            latestEvaluation={latestEvaluation}
            onClick={() => {
              onDownloadProfile(
                props.learnerId,
                latestProfile?.id ?? '',
                getSupportedDownloadDialect(
                  latestProfile?.profileType,
                  props.preferredDialect,
                ),
              );
            }}
          />
        );
      case 'organisation':
        return (
          <Link
            component={RouterLink}
            to={`/organisations/${latestEvaluation?.organisation.id}`}
          >
            <Box
              sx={{
                display: 'flex',
              }}
            >
              <TextV2 variant="bodyBold" color="primary">
                {latestEvaluation?.organisation.name}
              </TextV2>
            </Box>
          </Link>
        );

      default:
        return columnValue;
    }
  };

  const multipleOrgColumnPredicate = (
    columnName: keyof (TeamLearnerResponse & {
      learnerId: string;
      colourScores: string;
      organisation: string;
    }),
    columnValue: string | number,
    props: Record<any, any>,
  ) => {
    const { fullName, learnerId, latestProfile, latestEvaluation } =
      props as TeamLearnerResponse & {
        learnerId: string;
      };

    switch (columnName) {
      case 'fullName':
        return (
          <StyledDiv data-testid="full-name-colour-energy">
            <Link
              data-testid="learner-detail-link"
              component={RouterLink}
              to={`/learners/${latestEvaluation.organisation.id}/${learnerId}`}
            >
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <TextV2 variant="bodyBold" color="primary">
                  {fullName}
                </TextV2>
              </Box>
            </Link>
            <ColourEnergyIndicator
              {...latestProfile?.colourScores?.conscious}
              data-testid="colour-energies"
            />
          </StyledDiv>
        );
      case 'primaryEmail':
        return (
          <LinkEmail
            sx={(theme) => ({
              fontWeight: `${theme.typography.fontWeightMedium} !important`,
            })}
            email={columnValue as string}
          />
        );

      case 'latestProfile':
        return (
          <LatestProfileText
            latestProfile={latestProfile}
            latestEvaluation={latestEvaluation}
            migrated={migrated}
            onClick={() => {
              onDownloadProfile(
                props.learnerId,
                latestProfile?.id ?? '',
                getSupportedDownloadDialect(
                  latestProfile?.profileType,
                  props.preferredDialect,
                ),
              );
            }}
          />
        );
      case 'organisation':
        return (
          <Link
            component={RouterLink}
            to={`/organisations/${latestEvaluation.organisation.id}`}
          >
            <Box
              sx={{
                display: 'flex',
              }}
            >
              <TextV2 variant="bodyBold" color="primary">
                {latestEvaluation?.organisation.name}
              </TextV2>
            </Box>
          </Link>
        );
      default:
        return columnValue;
    }
  };

  const columnPredicate =
    type === 'SINGLE' ? singleOrgColumnPredicate : multipleOrgColumnPredicate;

  const removeUncheckedLearners = (
    current: Record<string, TransformedLearnerResponse>,
    checkedMap: Record<string, boolean>,
  ) => {
    const next = { ...current };
    Object.entries(current).forEach(([id]) => {
      if (!checkedMap[id]) delete next[id];
    });

    return next;
  };

  const handleSetCheckedItems = (checkedItemsMap: Record<string, boolean>) => {
    setCheckedItems?.((prev) => {
      const next = removeUncheckedLearners(prev, checkedItemsMap);

      Object.entries(checkedItemsMap)
        .filter(([id, isChecked]) => isChecked && !next[id])
        .forEach(([id]) => {
          const learner = learners.find((entry) => entry.id === id);
          if (!learner) return;
          next[id] = learner;
        });
      return next;
    });
  };

  const dataTableActions = migrated
    ? []
    : [
        <RemoveTeamMember
          id=""
          key="remove-button"
          onClick={handleRemoveTeamMember}
        />,
      ];

  const notSelectableStyles = {
    'tr > th:first-of-type': {
      verticalAlign: 'top',
    },

    'tr > td:first-of-type': {
      verticalAlign: 'top',
    },
  };
  const selectableStyles = {
    'tr > th:first-of-type': {
      verticalAlign: 'top',
      paddingRight: '0',
      paddingLeft: '0',
      minWidth: '0',
      maxWidth: '42px',
      width: '42px',
    },
    'tr > th:nth-of-type(2)': {
      paddingLeft: '0',
    },
    'tr > td:first-of-type': {
      verticalAlign: 'top',
      paddingRight: '0',
      paddingLeft: '0',
    },
    'tr > td:nth-of-type(2)': {
      paddingLeft: '0',
    },
  };
  return (
    <>
      <StyledTable
        sx={selectable ? selectableStyles : notSelectableStyles}
        columnPredicate={(column, columnValue, props) =>
          columnPredicate(
            column as
              | 'learnerId'
              | keyof TeamLearnerResponse
              | 'colourScores'
              | 'organisation',
            columnValue,
            props,
          )
        }
        checkedItems={booleanizeCheckedItems(checkedItems ?? {})}
        onCheckedItems={handleSetCheckedItems}
        dataStructure={dataStructure}
        selectable={selectable}
        actions={dataTableActions}
        onPageChange={onPageChange}
        paginator={paginator}
        apiPaginationProps={apiPaginationProps}
      />
      <DownloadProgressDialog
        open={showDownloadProfileDialog}
        onClose={() => setShowDownloadProfileDialog(false)}
      />
    </>
  );
};

const LearnersList = ({
  teamId,
  checkedItems,
  setCheckedItems,
  removeCheckedItems,
  evaluationIds,
  migrated,
  type = 'SINGLE',
  selectable,
  onPageChange,
  paginator,
  apiPaginationProps,
}: LearnersListProps) => {
  const { pagedItems: pagedLearners } = paginator;
  if (pagedLearners.length > 0) {
    return (
      <TeamLearners
        evaluationIds={evaluationIds}
        teamId={teamId}
        checkedItems={checkedItems}
        setCheckedItems={setCheckedItems}
        removeCheckedItems={removeCheckedItems}
        migrated={migrated}
        selectable={selectable}
        type={type}
        onPageChange={onPageChange}
        paginator={paginator}
        apiPaginationProps={apiPaginationProps}
      />
    );
  }

  return (
    <StyledListItemWrapper>
      <LearnersListEmptyMsg />
    </StyledListItemWrapper>
  );
};

export default LearnersList;
